'use client'

import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ChevronDown } from 'lucide-react'

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  toast,
} from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import type { Student } from '../types'
import { SchoolClassAvatar } from '../../../components/school-class-avatar'
import { useSharedTRPC } from '../../../context'
import { useStudentMutations } from '../use-student-mutations'

/**
 * `listChildren`'s `schoolClasses` has no `ORDER BY`, so Postgres may hand the same memberships
 * back in a different order between requests. Sorting once here keeps the trigger label, the `+N`
 * overflow count, and the column sort key all derived from the same ordering.
 */
export function getSortedSchoolClasses(student: Student) {
  return [...(student.schoolClasses ?? [])].sort((a, b) => a.name.localeCompare(b.name))
}

interface ClassCellProps {
  student: Student
  canEdit: boolean
}

/**
 * Inline class picker for one student row.
 *
 * Multi-select, because `team_member` is many-to-many and always has been: the trigger could
 * already render "3A +1", while the picker underneath it was a radio group that could only ever
 * express one class. Picking a second class did not add it — `editChild({ teamId })` replaces the
 * whole set — so the only way to reach two classes was the Kelola kelas page. `setChildTeams`
 * writes the array this cell now edits.
 *
 * Ticks are held in local draft state and flushed once when the menu closes, not per tick. Firing
 * one set-replacement per checkbox would race a burst of clicks against each other, and a
 * late-arriving response would resurrect a class the user had just unticked.
 */
export function ClassCell({ student, canEdit }: ClassCellProps) {
  const trpc = useSharedTRPC()
  const { invalidate } = useStudentMutations()

  const { data: teams } = useQuery(trpc.myOrganization.getAllTeams.queryOptions())
  const setChildTeams = useMutation(trpc.myOrganization.setChildTeams.mutationOptions())

  const classes = getSortedSchoolClasses(student)

  // `null` means "no local edit in flight": the cell then renders straight from the query, so a
  // refetch or an edit made elsewhere shows up instead of being shadowed by a stale draft.
  const [draft, setDraft] = useState<string[] | null>(null)
  const selected = draft ?? classes.map((schoolClass) => schoolClass.id)

  // A class ticked during this session isn't in `student.schoolClasses` yet, so its name and avatar
  // have to come from the org-wide list for the trigger to label it before the write lands.
  const known = new Map<string, { id: string; name: string; image?: string | null }>(
    [...(teams ?? []), ...classes].map((schoolClass) => [schoolClass.id, schoolClass]),
  )
  const displayed = selected
    .map((id) => known.get(id))
    .filter((schoolClass) => schoolClass !== undefined)
    .sort((a, b) => a.name.localeCompare(b.name))

  const current = displayed[0] ?? null
  const overflow = Math.max(displayed.length - 1, 0)

  const toggle = (teamId: string) => {
    setDraft(selected.includes(teamId) ? selected.filter((id) => id !== teamId) : [...selected, teamId])
  }

  const flush = async (next: string[]) => {
    const before = classes.map((schoolClass) => schoolClass.id)
    if (next.length === before.length && next.every((id) => before.includes(id))) {
      setDraft(null)
      return
    }

    try {
      await setChildTeams.mutateAsync({ userId: student.id, teamIds: next })
      await invalidate()

      const names = displayed.map((schoolClass) => schoolClass.name)
      toast.success(
        names.length === 0 ? `${student.name} sekarang tanpa kelas.` : `Kelas ${student.name}: ${names.join(', ')}.`,
      )
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Gagal memperbarui kelas')
    } finally {
      // Held until the write settles so the trigger shows what the user picked while it saves, then
      // handed back to the query — which is now the source of truth either way.
      setDraft(null)
    }
  }

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (!open) void flush(selected)
      }}
    >
      <DropdownMenuTrigger
        disabled={!canEdit || setChildTeams.isPending}
        render={
          <button
            type='button'
            className={cn(
              typographyVariants({ variant: 'body-md' }),
              'srd:text-neutral-black srd:flex srd:w-full srd:min-w-[110px] srd:items-center srd:justify-between srd:gap-x-2 srd:text-nowrap srd:disabled:cursor-not-allowed',
            )}
          >
            <span className='srd:flex srd:items-center srd:gap-x-1.5'>
              {current && <SchoolClassAvatar name={current.name} image={current.image} />}
              {current ? current.name : '-'}
              {overflow > 0 && (
                <span className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-grey-100')}>+{overflow}</span>
              )}
            </span>
            <ChevronDown className='srd:text-grey-100 srd:size-4 srd:shrink-0' />
          </button>
        }
      />

      <DropdownMenuContent align='start' className='srd:max-h-[280px] srd:overflow-y-auto'>
        {/* "Tanpa kelas" is the empty selection rather than a class of its own: ticked when nothing
            else is, and picking it clears the rest instead of toggling itself off. */}
        <DropdownMenuCheckboxItem
          checked={selected.length === 0}
          onCheckedChange={() => setDraft([])}
          className='srd:data-checked:bg-blue-10'
        >
          Tanpa kelas
        </DropdownMenuCheckboxItem>

        {(teams?.length ?? 0) > 0 && <DropdownMenuSeparator />}

        {(teams ?? []).map((team) => (
          <DropdownMenuCheckboxItem
            key={team.id}
            checked={selected.includes(team.id)}
            onCheckedChange={() => toggle(team.id)}
            className='srd:data-checked:bg-blue-10'
          >
            <SchoolClassAvatar name={team.name} image={team.image} />
            <span className='srd:truncate'>{team.name}</span>
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
